import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eye, Loader2, FileText, Image, File, AlertCircle } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from './ui/hover-card';

const DocumentPreviewButton = ({ documentId, filename, api, onViewClick }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [fileType, setFileType] = useState('unknown');
  const [isOpen, setIsOpen] = useState(false);

  // Determine file type from filename
  const getFileType = (name) => {
    if (!name) return 'unknown';
    const ext = name.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    return 'unknown';
  };

  useEffect(() => {
    setFileType(getFileType(filename));
  }, [filename]);

  const loadPreview = async () => {
    if (previewUrl || previewLoading) return;
    
    setPreviewLoading(true);
    setPreviewError(null);
    
    try {
      const response = await api().get(`/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && detail.includes('re-upload')) {
        setPreviewError('File not found. Please re-upload.');
      } else if (error.response?.status === 404) {
        setPreviewError('Document not found on server.');
      } else {
        setPreviewError('Failed to load preview');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && !previewUrl && !previewError) {
      loadPreview();
    }
  };

  const handleViewClick = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    } else {
      onViewClick?.();
    }
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'image':
        return <Image className="w-6 h-6 text-blue-500" />;
      case 'pdf':
        return <FileText className="w-6 h-6 text-red-500" />;
      case 'word':
        return <FileText className="w-6 h-6 text-blue-600" />;
      case 'excel':
        return <FileText className="w-6 h-6 text-green-600" />;
      default:
        return <File className="w-6 h-6 text-gray-500" />;
    }
  };

  return (
    <HoverCard openDelay={300} closeDelay={100} open={isOpen} onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleViewClick}
          title="View Document"
        >
          <Eye className="w-4 h-4" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent 
        className="w-80 p-2" 
        side="left"
        align="center"
      >
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium truncate">
            {getFileIcon()}
            <span className="truncate">{filename}</span>
          </div>
          
          {previewLoading && (
            <div className="flex items-center justify-center h-40 bg-muted rounded-lg">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
          
          {previewError && (
            <div className="flex flex-col items-center justify-center h-40 bg-red-50 dark:bg-red-950 rounded-lg text-center p-4">
              <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
              <p className="text-sm text-red-600 dark:text-red-400">{previewError}</p>
            </div>
          )}
          
          {previewUrl && !previewLoading && !previewError && (
            <>
              {fileType === 'image' ? (
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt={filename}
                    className="w-full h-40 object-contain rounded-lg bg-muted"
                  />
                </div>
              ) : fileType === 'pdf' ? (
                <div className="relative h-40 rounded-lg overflow-hidden border">
                  <iframe 
                    src={`${previewUrl}#toolbar=0&navpanes=0`}
                    className="w-full h-full"
                    title={filename}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-40 bg-muted rounded-lg">
                  {getFileIcon()}
                  <p className="text-sm text-muted-foreground mt-2">Preview not available</p>
                  <p className="text-xs text-muted-foreground">Click to download</p>
                </div>
              )}
            </>
          )}
          
          <p className="text-xs text-muted-foreground text-center">
            Click to open in new tab
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default DocumentPreviewButton;
